// Inline scheduling form shown under a content-history row.
// Owns the date + auto-post platform toggles locally; reports the final choice
// to the page via onConfirm(opts). Shows "posts as @you" hints based on the
// user's personal LinkedIn / X connections.
import {
  useState, Link, Button, Input, Check, X, Twitter, Linkedin, Facebook, Instagram, Video,
} from "@/pages/shared";

export const ScheduleControls = ({ item, linkedinConn, twitterConn, onConfirm, onCancel }) => {
  const [scheduleDate, setScheduleDate] = useState("");
  const [autoPostX, setAutoPostX] = useState(true); // default ON — most-used channel
  const [autoPostLI, setAutoPostLI] = useState(false);
  const [autoPostFB, setAutoPostFB] = useState(false);
  const [autoPostIG, setAutoPostIG] = useState(false);

  return (
    <div className="flex flex-col gap-2 mt-2">
      <div className="flex items-center gap-2 flex-wrap">
        <Input type="datetime-local" value={scheduleDate} onChange={(e) => setScheduleDate(e.target.value)} className="bg-white/5 border-white/10 text-sm h-8" />
        <span className="text-[10px] text-muted-foreground" data-testid={`tz-hint-${item.id}`}>
          {Intl.DateTimeFormat().resolvedOptions().timeZone}
        </span>
        <Button size="sm" onClick={() => onConfirm({ scheduleDate, autoPostX, autoPostLI, autoPostFB, autoPostIG })} className="h-8 bg-teal-500 hover:bg-teal-600" data-testid={`confirm-schedule-${item.id}`}><Check className="w-3 h-3" /></Button>
        <Button size="sm" variant="ghost" onClick={onCancel} className="h-8"><X className="w-3 h-3" /></Button>
      </div>
      <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer select-none" data-testid={`auto-post-toggle-${item.id}`}>
        <input
          type="checkbox"
          checked={autoPostX}
          onChange={(e) => setAutoPostX(e.target.checked)}
          className="accent-violet-500"
        />
        <Twitter className="w-3 h-3 text-blue-400" />
        Also auto-post to X at this time
        {autoPostX && twitterConn?.connected && (
          <span className="text-teal-400 ml-1" data-testid={`x-posts-as-${item.id}`}>
            → posts as <span className="font-medium">@{twitterConn.screen_name}</span>
          </span>
        )}
        {autoPostX && !twitterConn?.connected && (
          <span className="text-blue-300 ml-1" data-testid={`x-admin-account-${item.id}`}>
            → via Creatara account (<Link to="/dashboard/settings" className="underline">connect your X</Link>)
          </span>
        )}
        {!autoPostX && !autoPostLI && !autoPostFB && !autoPostIG && (
          <span className="text-blue-300 ml-1">(we'll email you a one-click-share reminder at that time)</span>
        )}
      </label>
      <div className="flex flex-wrap gap-x-4 gap-y-1 pl-1">
        <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer select-none" data-testid={`auto-post-li-${item.id}`}>
          <input type="checkbox" checked={autoPostLI} onChange={(e) => setAutoPostLI(e.target.checked)} className="accent-blue-600" />
          <Linkedin className="w-3 h-3 text-blue-600" />LinkedIn
          {autoPostLI && linkedinConn?.connected && (
            <span className="text-teal-400 ml-1" data-testid={`li-posts-as-${item.id}`}>
              → posts as <span className="font-medium">{linkedinConn.name}</span>
            </span>
          )}
          {autoPostLI && !linkedinConn?.connected && (
            <span className="text-amber-400 ml-1" data-testid={`li-not-connected-${item.id}`}>
              → reminder email only (<Link to="/dashboard/settings" className="underline">connect LinkedIn</Link>)
            </span>
          )}
        </label>
        <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer select-none" data-testid={`auto-post-fb-${item.id}`}>
          <input type="checkbox" checked={autoPostFB} onChange={(e) => setAutoPostFB(e.target.checked)} className="accent-blue-500" />
          <Facebook className="w-3 h-3 text-blue-500" />Facebook Page
          <span className="text-amber-400/80 ml-1">(via Creatara's shared Page — your own Page pending Meta review)</span>
        </label>
        <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer select-none" data-testid={`auto-post-ig-${item.id}`}>
          <input
            type="checkbox"
            checked={autoPostIG}
            onChange={(e) => setAutoPostIG(e.target.checked)}
            disabled={!item.image_url}
            className="accent-pink-500"
          />
          <Instagram className={`w-3 h-3 ${item.image_url ? "text-pink-500" : "text-muted-foreground/50"}`} />
          Instagram {!item.image_url && <span className="text-amber-400/80 ml-1">(needs image)</span>}
          <span className="text-amber-400/80 ml-1">(via Creatara's shared account — yours pending Meta review)</span>
        </label>
      </div>
      {item.video_url && (
        <span className="text-xs text-cyan-300 flex items-center gap-1" data-testid={`schedule-video-hint-${item.id}`}>
          <Video className="w-3 h-3" /> Will post with video
        </span>
      )}
    </div>
  );
};
